import each from 'lodash/each';
import { authenticationReady, componentLoader, configurationReady, mdsCssReady, mdsIconsReady, loadFiles, manifestReady, setDefaultValues } from 'loader-base';
import configurationReadyQs from './configuration-qs';
import ecReady from './initialize-ec';
import { initializeLazyLoader, lazyDownloadComponent, registerPaths } from './lazy-loader';
import bundlesReady from './bundle-loader';
import { installTrackingAgent } from './usage-tracking-helper';

let lazyDownloading = false;

/**
 * Add client custom stylesheets
 * @param {*} manifest
 */
function loadCustomStyles(manifest) {
    return loadFiles(manifest.loaderConfig.css.customStyles)
        .then(() => Promise.resolve(manifest));
}

function checkAndInitializeMorningstarUsageTracking(loaderConfig) {
    if (loaderConfig.morningstarUsageTracking && loaderConfig.morningstarUsageTracking.enabled) {
        installTrackingAgent();
    }
}

function load(loaderConfig = {}) {
    setDefaultValues(loaderConfig);
    if (loaderConfig.lazy && (loaderConfig.lazy.lazyDownloadComponent || loaderConfig.lazy.lazyLoadComponent)) {
        loaderConfig.lazy.lazyLoadComponent = true;
        loaderConfig.angular.bootstrap = false;
    }

    let promiseChain = Promise.resolve(loaderConfig);

    // NewRelic agent needs to be installed in <head> section
    // this should be done in the sync code and not wait for download of config or bundle
    checkAndInitializeMorningstarUsageTracking(loaderConfig);

    switch (loaderConfig.configuration.source.type) {
        case 'service': {
            promiseChain = promiseChain.then(loaderConfig => authenticationReady(loaderConfig))
                .then(loaderConfig => configurationReady(loaderConfig));
            break;
        }
        case 'qs':
        case 'mwc': {
            promiseChain = promiseChain.then(loaderConfig => configurationReadyQs(loaderConfig));
            break;
        }
        case 'none':
        default: {
            promiseChain = promiseChain.then(loaderConfig => configurationReady(loaderConfig));
            break;
        }
    }

    promiseChain = promiseChain.then(loaderConfig => mdsIconsReady(loaderConfig))
        .then(loaderConfig => mdsCssReady(loaderConfig));

    switch (loaderConfig.componentSource.type) {
        case 'resolver': {
            const asterixSetupFilter = function asterixSetupFilter(file) {
                return (file.indexOf('angular') > -1 || file.indexOf('asterix-') > -1 || file.indexOf('jquery') > -1 || file.indexOf('vue') > -1 || file.indexOf('underscore') > -1);
            };
            const mwsFilter = function mwsFilter(file) {
                return file.indexOf('vue-') < 0 && file.indexOf('mws') < 0;
            };
            const antiMwsFilter = function antiMwsFilter(file) {
                return file.indexOf('vue-') > -1;
            };
            promiseChain = promiseChain.then(loaderConfig => manifestReady(loaderConfig, loaderConfig.config, true))
                .then(manifest => componentLoader.componentInitializationReady(manifest));

            if (loaderConfig.lazy.lazyDownloadComponent) {
                lazyDownloading = true;
                promiseChain = promiseChain.then(manifest => initializeLazyLoader(manifest))
                    .then(manifest => componentLoader.componentsReady(manifest, asterixSetupFilter))
                    .then(manifest => ecReady(manifest))
                    .then(manifest => componentLoader.mwsReady(manifest));
            } else {
                promiseChain = promiseChain.then(manifest => componentLoader.componentsReady(manifest, mwsFilter))
                    .then(manifest => ecReady(manifest))
                    .then(manifest => componentLoader.mwsReady(manifest)) // Needs to be loaded after asterix so the config module can be reused.
                    .then(manifest => componentLoader.componentsReady(manifest, antiMwsFilter));
            }
            break;
        }
        case 'qs':
        case 'mwc':
        default: {
            promiseChain = promiseChain.then(loaderConfig => bundlesReady(loaderConfig, componentLoader.getLoadedFiles())).then(manifest => ecReady(manifest));
            break;
        }
    }

    promiseChain = promiseChain.then(manifest => loadCustomStyles(manifest));

    return promiseChain;
}

function reset() {
    componentLoader.resetInternal();
}

function lazyLoad(containerParent, containerId, componentTagName, componentId) {
    if (lazyDownloading) {
        return new Promise((resolve) => {
            lazyDownloadComponent(componentTagName, componentLoader.getLoadedFiles()).then(() => {
                morningstar.asterix.config.setOption('dynamic', true);
                // Create component tag...
                const componentTag = document.createElement(componentTagName);
                componentTag.setAttribute('mstar-component-id', componentId);
                // ... and attach it to the container.
                const container = document.createElement('div');
                container.setAttribute('id', containerId);
                container.appendChild(componentTag);
                containerParent.appendChild(container);
                // Register default config for component types that need it.
                each(morningstar.components._defaultConfig, (defConfig, typeName) => {
                    if (!morningstar.asterix.config.getDefaultConfig(typeName)) {
                        morningstar.asterix.config.addType(typeName);
                    }
                });
                // Register component paths in Asterix.
                registerPaths(componentId);
                // Handle Asterix reset/init.
                const path = morningstar.asterix.instanceRegistry.find(componentId).modulePath;
                morningstar.asterix.instanceRegistry.init(path);
                // Bootstrat the element
                angular.bootstrap(document.getElementById(containerId), morningstar.asterix.ng.getAngularModuleDependencies());
                resolve();
            });
        });
    }

    return new Promise((resolve) => {
        // Handle Asterix reset/init.
        morningstar.asterix.instanceRegistry.init();
        // Create component tag...
        const componentTag = document.createElement(componentTagName);
        componentTag.setAttribute('mstar-component-id', componentId);
        // ... and attach it to the container.
        const container = document.createElement('div');
        container.setAttribute('id', containerId);
        container.appendChild(componentTag);
        containerParent.appendChild(container);
        // Bootstrap the element
        angular.bootstrap(document.getElementById(containerId), morningstar.asterix.ng.getAngularModuleDependencies());
        resolve();
    });
}

function lazyRemove(containerId) {
    const container = document.getElementById(containerId);
    if (container && container.remove) {
        container.remove();
        console.log(`Removed container with id: ${containerId}`);
    }
    // Because IE
    if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        console.log(`Removed container with id: ${containerId} (IE style`);
    }
}

export default {
    lazyLoad,
    lazyRemove,
    load,
    reset,
};



// WEBPACK FOOTER //
// ./src/loader.js